import { getColorClass, getBackgroundColorClass } from "@/lib/colors";
import type { TriggerEventContext, TriggerRenderer, TriggerRendererContext } from "../types";
import githubIcon from "@/assets/icons/integrations/github.svg";
import type { TriggerProps } from "@/ui/trigger";
import type { BaseNodeMetadata, PullRequest } from "./types";
import { buildGithubSubtitle } from "./utils";

interface OnPRReviewConfiguration {
  contentFilter?: string;
}

interface OnPRReviewEventData {
  action?: string;
  pull_request?: PullRequest;
  review?: {
    body?: string;
    html_url?: string;
    user?: {
      login?: string;
    };
  };
  review_comments?: Array<{
    body?: string;
    path?: string;
  }>;
}

function buildOnPRReviewMetadataItems(metadata?: BaseNodeMetadata, configuration?: OnPRReviewConfiguration) {
  const metadataItems = [];

  if (metadata?.repository?.name) {
    metadataItems.push({
      icon: "book",
      label: metadata.repository.name,
    });
  }

  if (configuration?.contentFilter) {
    metadataItems.push({
      icon: "funnel",
      label: `Filter: ${configuration.contentFilter}`,
    });
  }

  return metadataItems;
}

export const onPRReviewTriggerRenderer: TriggerRenderer = {
  getTitleAndSubtitle: (context: TriggerEventContext) => {
    const eventData = context.event?.data as OnPRReviewEventData;
    const prNumber = eventData?.pull_request?.number || "";

    return {
      title: `#${prNumber} - PR Review`,
      subtitle: buildGithubSubtitle(`By ${eventData?.review?.user?.login || "unknown"}`, context.event?.createdAt),
    };
  },

  getRootEventValues: (context: TriggerEventContext): Record<string, string> => {
    const eventData = context.event?.data as OnPRReviewEventData;

    return {
      "Received At": context.event?.createdAt ? new Date(context.event.createdAt).toLocaleString() : "-",
      Author: eventData?.review?.user?.login || "-",
      "Review Body": eventData?.review?.body || "-",
      "Review URL": eventData?.review?.html_url || "-",
      "Inline Comments": eventData?.review_comments?.length?.toString() || "0",
      "PR Number": eventData?.pull_request?.number?.toString() || "-",
      "PR Title": eventData?.pull_request?.title || "-",
      "PR URL":
        eventData?.pull_request?.html_url ||
        eventData?.pull_request?._links?.html?.href ||
        eventData?.pull_request?.url ||
        "-",
      "Branch Name": eventData?.pull_request?.head?.ref || "-",
      "Head SHA": eventData?.pull_request?.head?.sha || "-",
    };
  },

  getTriggerProps: (context: TriggerRendererContext) => {
    const { node, definition, lastEvent } = context;
    const metadata = node.metadata as unknown as BaseNodeMetadata;
    const configuration = node.configuration as unknown as OnPRReviewConfiguration;

    const props: TriggerProps = {
      title: node.name || definition.label || "Unnamed trigger",
      iconSrc: githubIcon,
      iconColor: getColorClass(definition.color),
      collapsedBackground: getBackgroundColorClass(definition.color),
      metadata: buildOnPRReviewMetadataItems(metadata, configuration),
    };

    if (lastEvent) {
      const eventData = lastEvent.data as OnPRReviewEventData;
      const prNumber = eventData?.pull_request?.number || "";

      props.lastEventData = {
        title: `#${prNumber} - PR Review`,
        subtitle: buildGithubSubtitle(`By ${eventData?.review?.user?.login || "unknown"}`, lastEvent.createdAt),
        receivedAt: new Date(lastEvent.createdAt),
        state: "triggered",
        eventId: lastEvent.id,
      };
    }

    return props;
  },
};
